
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface EmailTemplateSelectorProps {
  onSelectTemplate: (template: string, data: any) => void;
}

export function EmailTemplateSelector({ onSelectTemplate }: EmailTemplateSelectorProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>("welcome");
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");

  const templates = [
    { id: "welcome", name: "Welcome Email" },
    { id: "waitlist_confirmation", name: "Waitlist Confirmation" },
    { id: "nursing_home_leads", name: "Nursing Home Leads" },
    { id: "claim_request", name: "Claim Request" },
    { id: "nursing_home_inquiry", name: "Nursing Home Inquiry" },
    { id: "guide_download", name: "Guide Download" },
    { id: "calculator_results", name: "Calculator Results" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic template data
    const templateData = {
      email,
      firstName,
      // Add additional template-specific fields as needed
      nursingHomes: ["Sample Nursing Home 1", "Sample Nursing Home 2"],
      dashboardUrl: "https://grandpal.co/dashboard",
      guideTitle: "Finding a Nursing Home in Ireland",
      guideUrl: "https://grandpal.co/guides/sample",
      county: "Dublin",
      nursingHomeName: "Sample Nursing Home",
      results: {
        weeklyContribution: 350,
        incomeContribution: { weekly: 200, annual: 10400 },
        assetContribution: { weekly: 150, annual: 7800 },
        deferredAmount: 100,
        stateSupport: 250
      },
      formData: {
        nursingHomeCost: 1200,
        useLoan: true
      },
      message: "I'm interested in finding out more about this nursing home for my father.",
      claimLink: "https://grandpal.co/claim/sample",
      preference: "both"
    };
    
    onSelectTemplate(
      selectedTemplate === "calculator_results" ? "calculator_results" : selectedTemplate, 
      templateData
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Email Template</CardTitle>
        <CardDescription>Choose a template to preview or test send</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <Label>Email Template</Label>
            <RadioGroup
              defaultValue={selectedTemplate}
              onValueChange={setSelectedTemplate}
              className="grid grid-cols-1 md:grid-cols-2 gap-2"
            >
              {templates.map((template) => (
                <div key={template.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={template.id} id={template.id} />
                  <Label htmlFor={template.id} className="cursor-pointer">
                    {template.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Test Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter>
        <Button 
          type="button" 
          onClick={handleSubmit} 
          className="w-full"
          disabled={!email || !firstName}
        >
          Preview Template
        </Button>
      </CardFooter>
    </Card>
  );
}
